import * as client from "../../../client";
import partition from "lodash/partition";
import { Dialog } from "primereact/dialog";
import styles from "./MapModal.module.css";
import { LayersControl } from "react-leaflet";
import {
  Circle,
  FeatureGroup,
  MapContainer,
  Marker,
  Polyline,
  TileLayer,
  useMap,
} from "react-leaflet";
import { format } from "date-fns";
import L, { LatLng } from "leaflet";
import { EditControl } from "react-leaflet-draw";
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SafeReportResponse } from "../Conversion";
import { InputSwitch } from "primereact/inputswitch";
import { rgbString } from "./colors";
import { RawAddress } from "../../../client";
import { InputNumber } from "primereact/inputnumber";
import { useAddressesForMap, useUploadAdditionalFiles } from "../data";
import { Icon } from "leaflet";
import { MapTable } from "./MapTable";
import { Plottable, RiskCluster, SinglePlottable } from "./types";
import { isEqual } from "lodash";
import { Divider } from "primereact/divider";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import {
  boundingBoxFromPoints,
  expandLatLonBoundingBoxByMiles,
  findAddressesInRadius,
  haversineDistanceMiles,
  MILES_TO_METERS,
  parseCoverage,
} from "./util";
import { formatExactMoneyDollars } from "../Money";
import { sleepMs } from "../../../util";
import shadowPng from "./images/marker-shadow.png";
import greenPng from "./images/marker-icon-green.png";
import redPng from "./images/marker-icon-red.png";
import bluePng from "./images/marker-icon-blue.png";
import { Button } from "primereact/button";
import DomToImage from "dom-to-image";
import { AddressSearch } from "./Search";
import { useToast } from "../../../context-providers/Toast";
import { LOB } from "../../../api/routes";

const DEBUG = false;

const redIcon = new Icon({
  iconUrl: redPng,
  shadowUrl: shadowPng,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
const blueIcon = new Icon({
  iconUrl: bluePng,
  shadowUrl: shadowPng,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
const greenIcon = new Icon({
  iconUrl: greenPng,
  shadowUrl: shadowPng,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const Cluster = ({
  cluster: c,
  isSelected,
  onClick,
}: {
  cluster: RiskCluster;
  isSelected: boolean;
  onClick: () => void;
}) => {
  const defaultColor = rgbString([100, 50, 50]);
  const selectedColor = rgbString([255, 20, 20]);
  return (
    <Fragment>
      <Circle
        center={[c.lat, c.lon]}
        radius={c.radius * MILES_TO_METERS}
        pathOptions={{ color: isSelected ? selectedColor : defaultColor }}
        key={`${c.lat}-${c.lon}`}
        eventHandlers={{ click: onClick }}
      />
      {DEBUG ? (
        <>
          <Polyline
            key={`${c.lat} ${c.lon} x`}
            positions={(c.debug?.seedPoints ?? []).map((s) => [s.lat, s.lon])}
          />
          {(c.debug?.perCircle ?? []).flatMap(({ midPoint, norms }) =>
            norms.map((n, i) => [
              <Polyline
                key={`${c.lat} ${c.lon} n${i}`}
                color="#FF0000"
                positions={[
                  [midPoint.lat, midPoint.lon],
                  [midPoint.lat + n.lat, midPoint.lon + n.lon],
                ]}
              />,
            ])
          )}
        </>
      ) : null}
    </Fragment>
  );
};

const SetBounds = ({
  minLat,
  minLon,
  maxLat,
  maxLon,
}: {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
}) => {
  const map = useMap();
  useEffect(() => {
    // Expand by a bit so everything is certainly in view
    const latBuffer = (maxLat - minLat) / 4;
    const longBuffer = (maxLon - minLon) / 4;
    map.fitBounds([
      [minLat - latBuffer, minLon - longBuffer],
      [maxLat + latBuffer, maxLon + longBuffer],
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
  }, [minLat, maxLat, minLon, maxLon]);
  return null;
};

export const spatialKeyAddressesToPlottable = (
  addresses: client.SpatialKeyAddress[]
): SinglePlottable[] => {
  return addresses
    .map((address): SinglePlottable | undefined => {
      const lat = address["Latitude(SK)"];
      const lon = address["Longitude(SK)"];
      const streetAddress = address["Street 2"]
        ? `${address["Street 1"]} ${address["Street 2"]}`
        : address["Street 1"];
      const city = address["City"];
      const state = address["State"];
      const zip = address["Zip"];
      const tiv = address["Total TIV"];
      if (lat && lon && streetAddress && city && state && zip && tiv) {
        return {
          type: "single",
          lat,
          lon,
          streetAddress,
          city,
          state,
          zip,
          tiv,
          coverages: {
            bpp: parseCoverage(address["BPP"]),
            bi: parseCoverage(address["BI"]) + parseCoverage(address["BI_EE"]),
            building: parseCoverage(address["BLDG"]),
            other:
              parseCoverage(address["TIB"]) +
              parseCoverage(address["SIGNS"]) +
              parseCoverage(address["FF"]) +
              parseCoverage(address["INV"]) +
              parseCoverage(address["CE"]) +
              parseCoverage(address["OPA"]),
          },
          isFromCurrentReport: false,
          policyNumber: address["Policy Number"],
          namedInsured: address["Named Insured"],
        };
      }
      return undefined;
    })
    .filter((a) => a !== undefined);
};

export const addressesToPlottable = (
  addresses: RawAddress[],
  companyName: string
): { plottables: SinglePlottable[]; unaccountedForTiv: number } => {
  let unaccountedForTiv: number = 0;
  const plottables = addresses
    .map(
      ({
        lat: rawLat,
        long: rawLong,
        bpp_value: rawBpp,
        building_value: rawBuilding,
        bi_incl_ee_value: rawBiEe,
        bi_excl_ee_value: rawBi,
        signs_value: rawSigns,
        tenant_betterments_value: rawTB,
        furniture_and_fixtures_value: rawFF,
        inventory_value: rawInv,
        contractors_equipment_value: rawCe,
        outdoor_property_and_autos_value: rawOpa,
        street_address: streetAddress,
        city,
        zip,
        state,
        tiv,
      }): SinglePlottable | undefined => {
        if (rawLat && rawLong) {
          const lat = parseFloat(rawLat);
          const long = parseFloat(rawLong);
          if (isNaN(lat) || isNaN(long)) {
            return undefined;
          }
          return {
            type: "single",
            lat,
            lon: long,
            streetAddress,
            city: city ?? "",
            state: state ?? "",
            zip: zip ?? "",
            tiv,
            coverages: {
              bpp: parseCoverage(rawBpp),
              bi: parseCoverage(rawBi) + parseCoverage(rawBiEe),
              building: parseCoverage(rawBuilding),
              other:
                parseCoverage(rawTB) +
                parseCoverage(rawSigns) +
                parseCoverage(rawFF) +
                parseCoverage(rawInv) +
                parseCoverage(rawCe) +
                parseCoverage(rawOpa),
            },
            isFromCurrentReport: true,
            policyNumber: undefined,
            namedInsured: companyName,
          };
        }
        unaccountedForTiv += tiv;
        return undefined;
      }
    )
    .filter((a) => a !== undefined);
  return { plottables: plottables, unaccountedForTiv: unaccountedForTiv };
};

const groupByLatLong = (data: SinglePlottable[]): Plottable[] => {
  const groupedPlottables: { [key: string]: Plottable } = {};

  for (const item of data) {
    const key = `${item.lat},${item.lon}`;

    const existing = groupedPlottables[key];
    if (existing) {
      if (existing.type === "single") {
        if (existing.isFromCurrentReport !== item.isFromCurrentReport) {
          console.warn("Grouping addresses from current report and not!");
        }
        groupedPlottables[key] = {
          type: "grouped",
          lat: item.lat,
          lon: item.lon,
          isFromCurrentReport: existing.isFromCurrentReport,
          group: [existing, item],
          tiv: existing.tiv + item.tiv,
          coverages: {
            bpp: existing.coverages.bpp + item.coverages.bpp,
            bi: existing.coverages.bi + item.coverages.bi,
            building: existing.coverages.building + item.coverages.building,
            other: existing.coverages.other + item.coverages.other,
          },
        };
      } else {
        existing.group.push(item);
        existing.tiv += item.tiv;
      }
    } else {
      groupedPlottables[key] = item;
    }
  }

  return Object.values(groupedPlottables);
};

const distanceTextForLatLngs = (latLngs: LatLng[]) => {
  if (latLngs && latLngs.length === 2) {
    const [a, b] = latLngs;
    if (a && b) {
      const distanceInMiles = a.distanceTo(b) / MILES_TO_METERS;
      return `Distance: ${distanceInMiles.toFixed(2)} miles`;
    }
  }
  if (latLngs && latLngs.length > 2) {
    return "Please draw a 2 point line to see distance";
  }
  return "";
};

export const MapModalForSubmission = ({
  specialtyPropertyInfo,
  visible,
  onHide,
}: {
  specialtyPropertyInfo: SafeReportResponse;
  visible: boolean;
  onHide: () => void;
}) => {
  const inforceAddresses = useAddressesForMap([
    specialtyPropertyInfo.lob as LOB,
  ]).data?.addresses;
  const toast = useToast();

  const { data: submissionAddresses, unaccountedForTiv } = useMemo(() => {
    const { plottables, unaccountedForTiv } = addressesToPlottable(
      specialtyPropertyInfo.report_json.sovs
        .filter((sov) => sov.is_enabled)
        .flatMap((sov) => sov.addresses),
      specialtyPropertyInfo.report_json.company_info.named_insured ??
        specialtyPropertyInfo.report_json.company_info.company_name
    );
    return {
      data: groupByLatLong(plottables),
      unaccountedForTiv: unaccountedForTiv,
    };
  }, [specialtyPropertyInfo]);

  useEffect(() => {
    if (unaccountedForTiv > 0 && visible) {
      sleepMs(1000).then(() =>
        toast.show({
          severity: "warn",
          summary: "Not All Addresses are Shown",
          detail: `Some addresses don't have a latitude or longitude and are therefore not represented on the map. The TIV of unaccounted for addresses is ${formatExactMoneyDollars(
            unaccountedForTiv,
            { withDollarSign: true, withRounding: true }
          )}.`,
          life: 10000,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
  }, [unaccountedForTiv, visible]);
  const title = `${specialtyPropertyInfo.report_json.company_info.company_name} Addresses`;

  const uploadFiles = useUploadAdditionalFiles(
    specialtyPropertyInfo.report_id,
    () => {},
    true
  );
  const handleScreenshotFiles = async (
    files: File[],
    onSuccess: () => void
  ) => {
    await uploadFiles.mutateAsync(files, { onSuccess });
  };

  return (
    <>
      <MapModal
        title={title}
        inforceAddresses={inforceAddresses ?? []}
        submissionAddresses={submissionAddresses}
        visible={visible}
        onHide={onHide}
        handleScreenshotFiles={handleScreenshotFiles}
      />
    </>
  );
};

export const MapModalForSearch = ({
  visible,
  onHide,
  lobs,
}: {
  visible: boolean;
  onHide: () => void;
  lobs: LOB[];
}) => {
  const allInforceAddresses = useAddressesForMap(lobs).data?.addresses;
  const [selectedPolicy, setSelectedPolicy] = useState<string | undefined>();
  const { selectedAddresses, inforceAddresses } = useMemo(() => {
    if (allInforceAddresses) {
      const [matched, unmatched] = partition(
        allInforceAddresses,
        (a) => a["Policy Number"] == selectedPolicy
      );
      const selectedAddresses = spatialKeyAddressesToPlottable(matched).map(
        (x) => ({ ...x, isFromCurrentReport: true })
      );
      return { selectedAddresses, inforceAddresses: unmatched };
    }
    return { selectedAddresses: [], inforceAddresses: [] };
  }, [allInforceAddresses, selectedPolicy]);

  return (
    <>
      {inforceAddresses ? (
        <MapModal
          title="Search bound policies"
          inforceAddresses={inforceAddresses}
          submissionAddresses={selectedAddresses}
          visible={visible}
          onHide={onHide}
          extraInputComponent={
            <AddressSearch onPolicyChange={setSelectedPolicy} lobs={lobs} />
          }
        />
      ) : null}
    </>
  );
};

export const MapModal = ({
  title,
  inforceAddresses,
  submissionAddresses: data,
  visible,
  onHide,
  handleScreenshotFiles,
  extraInputComponent,
}: {
  title: string;
  inforceAddresses: client.SpatialKeyAddress[];
  submissionAddresses?: Plottable[];
  visible: boolean;
  onHide: () => void;
  handleScreenshotFiles?: (
    files: File[],
    onSuccess: () => void
  ) => Promise<void>;
  extraInputComponent?: React.ReactNode;
}) => {
  const toast = useToast();
  const inforceData = useMemo(() => {
    return groupByLatLong(
      spatialKeyAddressesToPlottable(inforceAddresses ?? [])
    );
  }, [inforceAddresses]);

  const submissionBbox = useMemo(
    () =>
      data && data.length > 0
        ? boundingBoxFromPoints(data)
        : boundingBoxFromPoints(inforceData),
    [data, inforceData]
  );
  const {
    lat: [minLat, maxLat],
    lon: [minLon, maxLon],
  } = submissionBbox;

  const [showInForcePolicies, setShowInForcePolicies] = useState(true);
  const [showClusters1, setShowClusters1] = useState(true);
  const [riskDiameter1, setRiskDiameter1] = useState(5);
  const [minTIV1, setMinTIV1] = useState(10_000_000);
  const [showClusters2, setShowClusters2] = useState(true);
  const [riskDiameter2, setRiskDiameter2] = useState(15);
  const [minTIV2, setMinTIV2] = useState(15_000_000);
  const [showClusters3, setShowClusters3] = useState(true);
  const [riskDiameter3, setRiskDiameter3] = useState(25);
  const [minTIV3, setMinTIV3] = useState(20_000_000);

  const addressesToShow = useMemo(
    () => (showInForcePolicies ? inforceData.concat(data ?? []) : data) ?? [],
    [showInForcePolicies, data, inforceData]
  );

  const [selectedPlottable, setSelectedPlottable] = useState<
    Plottable | undefined
  >(undefined);
  const [selectedCluster, setSelectedCluster] = useState<
    RiskCluster | undefined
  >(undefined);

  // Reset one if the other changes
  useEffect(() => {
    if (selectedPlottable) {
      setSelectedCluster(undefined);
    }
  }, [selectedPlottable]);
  useEffect(() => {
    if (selectedCluster) {
      setSelectedPlottable(undefined);
    }
  }, [selectedCluster]);

  const clusters = useMemo(() => {
    const radii: { [key: string]: number } = {};
    if (showClusters1) {
      radii["first"] = riskDiameter1 / 2;
    }
    if (showClusters2) {
      radii["second"] = riskDiameter2 / 2;
    }
    if (showClusters3) {
      radii["third"] = riskDiameter3 / 2;
    }

    const minTIVs: { [type: string]: number } = {
      first: minTIV1,
      second: minTIV2,
      third: minTIV3,
      singleton: Math.min(minTIV1, minTIV2, minTIV3),
    };

    const preFilter = (cluster: RiskCluster) => {
      return (
        cluster.included.some((a) => a.isFromCurrentReport) &&
        cluster.tiv >= (minTIVs[cluster.clusterType] ?? 0)
      );
    };

    const maxRadius = Math.max(...Object.values(radii));
    const bbox = Number.isFinite(maxRadius)
      ? expandLatLonBoundingBoxByMiles(submissionBbox, maxRadius * 2)
      : undefined;

    const input = bbox
      ? addressesToShow.filter(
          (a) =>
            a.lat >= bbox.lat[0] &&
            a.lat <= bbox.lat[1] &&
            a.lon >= bbox.lon[0] &&
            a.lon <= bbox.lon[1]
        )
      : addressesToShow;

    return findAddressesInRadius(input, radii, preFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
  }, [
    showClusters1,
    showClusters2,
    showClusters3,
    riskDiameter1,
    riskDiameter2,
    riskDiameter3,
    addressesToShow,
    submissionBbox,
  ]);

  const showSuccessSnackbar = () => {
    toast.show({
      severity: "success",
      summary: "Screenshot Saved",
      detail: 'You can find this screenshot on the "Attachments" tab.',
      life: 5000,
    });
  };

  const showFailureSnackbar = () => {
    toast.show({
      severity: "warn",
      summary: "Failed to Save Screenshot",
      detail: "Something went wrong while saving this screenshot.",
      life: 5000,
    });
  };

  const [isTakingScreenshot, setIsTakingScreenshot] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const captureScreenshot = handleScreenshotFiles
    ? async () => {
        setIsTakingScreenshot(true);
        try {
          if (!mapRef.current || !tableRef.current) {
            throw new Error("No map or table found");
          }

          // Create a temporary container div
          const tempContainer = document.createElement("div");
          tempContainer.style.display = "flex";
          tempContainer.style.flexDirection = "column";
          tempContainer.style.gap = "16px";
          tempContainer.style.position = "absolute";
          tempContainer.style.top = "0px";
          tempContainer.style.left = "0px";
          tempContainer.style.paddingTop = "20px";
          tempContainer.style.background = "var(--surface-card)";
          tempContainer.style.width = `calc(100vw - 72px)`;
          // We need to use z index instead of moving far to one side.
          // For whatever reason that doesn't work with dom to image
          // This does mean that it appears a bit behind the modal
          tempContainer.style.zIndex = "-1";

          // Clone the elements to avoid affecting the original DOM
          const mapClone = mapRef.current.cloneNode(true) as HTMLElement;
          const tableClone = tableRef.current.cloneNode(true) as HTMLElement;

          mapClone.style.marginLeft = "20px";
          mapClone.style.marginRight = "20px";

          tableClone.style.display = "block";

          tempContainer.appendChild(mapClone);
          tempContainer.appendChild(tableClone);

          const containerContainer = document.createElement("div");
          containerContainer.style.position = "absolute";
          containerContainer.style.right = "10000px";
          containerContainer.appendChild(tempContainer);
          document.body.appendChild(containerContainer);

          const dataUrl = await DomToImage.toPng(tempContainer, {
            quality: 1.0,
          });
          document.body.removeChild(containerContainer);

          const fetchResponse = await fetch(dataUrl);
          const blob = await fetchResponse.blob();
          const formattedDateTime = format(new Date(), "MM-dd-yyyy_HH:mm:ss");
          const leadName = "tiv-accumulation";
          const filename = `${leadName}_${formattedDateTime}.png`
            .replaceAll(" ", "-")
            .replace(/[^a-zA-Z0-9_\-.:]/g, "");
          const file = new File([blob], filename, { type: "image/png" });

          // Our catch should get the errors
          await handleScreenshotFiles([file], () => showSuccessSnackbar());
        } catch (error) {
          showFailureSnackbar();
        } finally {
          setIsTakingScreenshot(false);
        }
      }
    : null;

  return (
    <>
      <Dialog
        header={
          <div style={{ display: "flex", alignItems: "center", gap: "32px" }}>
            <span>{title}</span>
            {captureScreenshot && (
              <Button
                icon="pi pi-camera"
                size="small"
                loading={isTakingScreenshot}
                label="Capture Screenshot"
                onClick={captureScreenshot}
              />
            )}
          </div>
        }
        visible={visible}
        style={{
          width: "calc(100vw - 72px)",
          height: "calc(100vh - 72px)",
          maxHeight: "none",
        }}
        onHide={onHide}
      >
        <div className={styles.content}>
          <div className={styles.content} ref={mapRef}>
            <MapAndControls
              minLat={minLat}
              minLon={minLon}
              maxLat={maxLat}
              maxLon={maxLon}
              addressesToShow={addressesToShow}
              clusters={clusters}
              selectedPlottable={selectedPlottable}
              setSelectedPlottable={setSelectedPlottable}
              selectedCluster={selectedCluster}
              setSelectedCluster={setSelectedCluster}
              showInForcePolicies={showInForcePolicies}
              setShowInForcePolicies={setShowInForcePolicies}
              minTIV1={minTIV1}
              minTIV2={minTIV2}
              minTIV3={minTIV3}
              setMinTIV1={setMinTIV1}
              setMinTIV2={setMinTIV2}
              setMinTIV3={setMinTIV3}
              showClusters1={showClusters1}
              showClusters2={showClusters2}
              showClusters3={showClusters3}
              setShowClusters1={setShowClusters1}
              setShowClusters2={setShowClusters2}
              setShowClusters3={setShowClusters3}
              riskDiameter1={riskDiameter1}
              riskDiameter2={riskDiameter2}
              riskDiameter3={riskDiameter3}
              setRiskDiameter1={setRiskDiameter1}
              setRiskDiameter2={setRiskDiameter2}
              setRiskDiameter3={setRiskDiameter3}
              showBoundPoliciesToggle={!extraInputComponent}
              extraInputComponent={extraInputComponent}
            />
          </div>
          <MapTable
            selectedCluster={selectedCluster}
            selectedLocation={selectedPlottable}
            allAddresses={addressesToShow}
          />
          <div className={styles.screenshotTable}>
            <MapTable
              selectedCluster={selectedCluster}
              selectedLocation={selectedPlottable}
              allAddresses={addressesToShow}
              isForScreenshot={true}
              tableRef={tableRef}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

type MapAndControlsProps = {
  minLat: number;
  minLon: number;
  maxLat: number;
  maxLon: number;
  addressesToShow: Plottable[];
  clusters: RiskCluster[];
  selectedPlottable: Plottable | undefined;
  setSelectedPlottable: Dispatch<SetStateAction<Plottable | undefined>>;
  selectedCluster: RiskCluster | undefined;
  setSelectedCluster: Dispatch<SetStateAction<RiskCluster | undefined>>;
  showInForcePolicies: boolean;
  setShowInForcePolicies: Dispatch<SetStateAction<boolean>>;
  minTIV1: number;
  minTIV2: number;
  minTIV3: number;
  setMinTIV1: Dispatch<SetStateAction<number>>;
  setMinTIV2: Dispatch<SetStateAction<number>>;
  setMinTIV3: Dispatch<SetStateAction<number>>;
  showClusters1: boolean;
  showClusters2: boolean;
  showClusters3: boolean;
  setShowClusters1: Dispatch<SetStateAction<boolean>>;
  setShowClusters2: Dispatch<SetStateAction<boolean>>;
  setShowClusters3: Dispatch<SetStateAction<boolean>>;
  riskDiameter1: number;
  riskDiameter2: number;
  riskDiameter3: number;
  setRiskDiameter1: Dispatch<SetStateAction<number>>;
  setRiskDiameter2: Dispatch<SetStateAction<number>>;
  setRiskDiameter3: Dispatch<SetStateAction<number>>;
  showBoundPoliciesToggle: boolean;
  extraInputComponent: React.ReactNode;
};

const MapAndControls: React.FC<MapAndControlsProps> = ({
  minLat,
  minLon,
  maxLat,
  maxLon,
  addressesToShow,
  clusters,
  selectedPlottable,
  setSelectedPlottable,
  selectedCluster,
  setSelectedCluster,
  showInForcePolicies,
  setShowInForcePolicies,
  minTIV1,
  minTIV2,
  minTIV3,
  setMinTIV1,
  setMinTIV2,
  setMinTIV3,
  showClusters1,
  showClusters2,
  showClusters3,
  setShowClusters1,
  setShowClusters2,
  setShowClusters3,
  riskDiameter1,
  riskDiameter2,
  riskDiameter3,
  setRiskDiameter1,
  setRiskDiameter2,
  setRiskDiameter3,
  showBoundPoliciesToggle,
  extraInputComponent,
}) => {
  const onClick = (address: Plottable) => {
    setSelectedPlottable((selected) => {
      if (selected === address) {
        return undefined;
      }

      return address;
    });
  };

  const selectClusterFromLayer = (layer: L.Circle) => {
    const radiusInMiles = layer.getRadius() * 0.000621371;
    const centerLatLng = layer.getLatLng();
    const center = {
      lat: centerLatLng.lat,
      lon: centerLatLng.lng,
    };
    const addressesInCluster = [];
    for (const address of addressesToShow) {
      const dist = haversineDistanceMiles(center, address);
      if (dist <= radiusInMiles) {
        addressesInCluster.push(address);
      }
    }

    setSelectedCluster({
      lat: center.lat,
      lon: center.lon,
      clusterType: "custom",
      radius: radiusInMiles,
      included: addressesInCluster,
      tiv: addressesInCluster.reduce((a, b) => a + b.tiv, 0.0),
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
  const randomForKey = useMemo(() => Date.now(), [clusters]);

  return (
    <>
      <MapContainer
        zoom={3}
        boundsOptions={{}}
        className={styles.map}
        style={{ height: "calc(100vh - 192px)" }}
      >
        <SetBounds
          minLat={minLat}
          minLon={minLon}
          maxLat={maxLat}
          maxLon={maxLon}
        />
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Street Map">
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satellite">
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution="Esri"
            />
          </LayersControl.BaseLayer>
        </LayersControl>
        {addressesToShow.map((address, i) => {
          const isSelected = isEqual(address, selectedPlottable);
          const icon = isSelected
            ? redIcon
            : address.isFromCurrentReport
              ? blueIcon
              : greenIcon;
          return (
            <Marker
              key={i}
              opacity={address.isFromCurrentReport ? 1.0 : 0.75}
              zIndexOffset={address.isFromCurrentReport ? 10000 : 0}
              position={[address.lat, address.lon]}
              eventHandlers={{
                click: () => onClick(address),
              }}
              icon={icon}
            />
          );
        })}

        {clusters
          // Make sure the smaller clusters are on top and therefore selectable
          .sort((a, b) => b.radius - a.radius)
          .map((c) => (
            <Cluster
              key={`${randomForKey}-${c.lat}-${c.lon}`}
              cluster={c}
              isSelected={isEqual(c, selectedCluster)}
              onClick={() => setSelectedCluster(c)}
            />
          ))}
        <FeatureGroup>
          <EditControl
            position="topright"
            onCreated={(e) => {
              const { layerType, layer } = e;

              if (layerType === "polyline") {
                const latlngs: LatLng[] = layer.getLatLngs();
                const distanceText = distanceTextForLatLngs(latlngs);
                layer.bindPopup(distanceText).openPopup();
              } else if (layerType === "circle") {
                // So that future clicks will work
                layer.on("click", () => selectClusterFromLayer(layer));
                // Also immediately calculate and show
                selectClusterFromLayer(layer);
              }
            }}
            onEdited={(e) => {
              const layersGroup = e.layers;
              let layerCount = 0;
              let lastLayer = null;

              // Iterate through each layer in the FeatureGroup
              layersGroup.eachLayer((layer: L.Layer) => {
                if (layer instanceof L.Polyline) {
                  const latlngs: LatLng[] = layer.getLatLngs() as LatLng[];
                  const distanceText = distanceTextForLatLngs(latlngs);
                  layer.setPopupContent(distanceText);
                } else if (layer instanceof L.Circle) {
                  layerCount++;
                  lastLayer = layer;
                }
              });

              if (layerCount === 1 && lastLayer) {
                selectClusterFromLayer(lastLayer);
              }
            }}
            draw={{
              rectangle: false,
              polyline: true,
              polygon: false,
              marker: false,
              circlemarker: false,
              circle: true,
            }}
          />
        </FeatureGroup>
      </MapContainer>
      <div className={styles.controls}>
        <div
          className={styles.controlRow}
          style={{ flexDirection: "row", alignItems: "center", gap: "8px" }}
        >
          {showBoundPoliciesToggle ? (
            <>
              <span>Show bound policies</span>
              <InputSwitch
                checked={showInForcePolicies}
                onChange={() => setShowInForcePolicies((v) => !v)}
              />
            </>
          ) : null}
          <div className={styles.controlRow}>{extraInputComponent}</div>
        </div>
        <Divider className={styles.divider} />
        <div className={styles.controlRow}>
          <span>Show risk clusters 1</span>
          <InputSwitch
            checked={showClusters1}
            onChange={() => setShowClusters1((v) => !v)}
          />
        </div>
        <div className={styles.controlRow}>
          <span>Diameter 1</span>
          <InputNumber
            className={styles.inputNumber}
            value={riskDiameter1}
            onValueChange={(e) => setRiskDiameter1(e.value ?? 0)}
            min={0.5}
            max={300}
            suffix=" mi"
          />
        </div>
        <div className={styles.controlRow}>
          <span>TIV 1</span>
          <InputNumber
            className={styles.inputNumber}
            value={minTIV1}
            onValueChange={(e) => setMinTIV1(e.value ?? 0)}
            min={1}
            prefix="$"
          />
        </div>
        <Divider className={styles.divider} />
        <div className={styles.controlRow}>
          <span>Show risk clusters 2</span>
          <InputSwitch
            checked={showClusters2}
            onChange={() => setShowClusters2((v) => !v)}
          />
        </div>
        <div className={styles.controlRow}>
          <span>Diameter 2</span>
          <InputNumber
            className={styles.inputNumber}
            value={riskDiameter2}
            onValueChange={(e) => setRiskDiameter2(e.value ?? 0)}
            min={0.5}
            max={300}
            suffix=" mi"
          />
        </div>
        <div className={styles.controlRow}>
          <span>TIV 2</span>
          <InputNumber
            className={styles.inputNumber}
            value={minTIV2}
            onValueChange={(e) => setMinTIV2(e.value ?? 0)}
            min={1}
            prefix="$"
          />
        </div>
        <Divider className={styles.divider} />
        <div className={styles.controlRow}>
          <span>Show risk clusters 3</span>
          <InputSwitch
            checked={showClusters3}
            onChange={() => setShowClusters3((v) => !v)}
          />
        </div>
        <div className={styles.controlRow}>
          <span>Diameter 3</span>
          <InputNumber
            className={styles.inputNumber}
            value={riskDiameter3}
            onValueChange={(e) => setRiskDiameter3(e.value ?? 0)}
            min={0.5}
            max={300}
            suffix=" mi"
          />
        </div>
        <div className={styles.controlRow}>
          <span>TIV 3</span>
          <InputNumber
            className={styles.inputNumber}
            value={minTIV3}
            onValueChange={(e) => setMinTIV3(e.value ?? 0)}
            min={1}
            prefix="$"
          />
        </div>
      </div>
    </>
  );
};
